import Phaser from 'phaser';
import { pokerClient } from '../index';
import { PokerTableState } from '../network/PokerClient';
import { AudioSettings, loadAudioSettings, onAudioSettingsChanged, saveAudioSettings } from '../utils/audioSettings';
import { DeckId, DeckSettings, TableTheme, loadDeckSettings, saveDeckSettings } from '../utils/deckSettings';
import {
  AVATAR_PRESET_KEYS,
  AvatarSettings,
  buildDiscordAvatarUrl,
  getSelectedAvatarValue,
  loadAvatarSettings,
  saveAvatarSettings,
} from '../utils/avatarSettings';
import { buildHoldemBackendUrl, POKER_API_BASE_URL } from '../config/runtime';

type VolumeSlider = {
  container: Phaser.GameObjects.Container;
  updateFromValue: (value: number) => void;
};

type AvatarOption = {
  id: string;
  container: Phaser.GameObjects.Container;
  frame: Phaser.GameObjects.Rectangle;
  image: Phaser.GameObjects.Image;
  placeholder?: Phaser.GameObjects.Container;
};

type HoldemGuildOption = {
  id: string;
  name: string;
  icon: string | null;
  iconUrl?: string | null;
  shortLabel?: string | null;
  planTier: string;
  isPremium: boolean;
  selectable: boolean;
  blockedReason: string | null;
};

type HoldemSessionUser = {
  userId: string;
  username: string;
  nickname?: string | null;
  guildId?: string | null;
  avatar?: string | null;
  discriminator?: string | null;
};

type HoldemMeResponse = {
  authenticated: boolean;
  guildSelectionRequired?: boolean;
  selectedGuildId?: string | null;
  user?: HoldemSessionUser;
  guilds?: HoldemGuildOption[];
};

export default class MenuScene extends Phaser.Scene {
  private connectionStatus!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;

  private audioSettings: AudioSettings = loadAudioSettings();
  private unsubscribeAudioSettings?: () => void;
  private deckSettings: DeckSettings = loadDeckSettings();
  private avatarSettings: AvatarSettings = loadAvatarSettings();
  private avatarOptions: AvatarOption[] = [];
  private discordAvatarOption: AvatarOption | null = null;
  private discordAvatarTextureKey: string | null = null;
  
  // PC Balance display
  private coinBalanceContainer!: Phaser.GameObjects.Container;
  private coinBalanceText!: Phaser.GameObjects.Text;
  
  // State
  private connected = false;
  private authenticated = false;
  private authSessionActive = false;
  private availableTables: PokerTableState[] = [];
  private selectedTableIndex = -1;
  private pendingSessionUser: HoldemSessionUser | null = null;
  
  // Server poker configuration
  private serverConfig: {
    smallBlind: number;
    bigBlind: number;
    minBuyin: number;
    maxBuyin: number;
    maxPlayers: number;
  } | null = null;
  
  // UI Groups
  private mainMenuGroup!: Phaser.GameObjects.Container;
  private tableListGroup!: Phaser.GameObjects.Container;
  private confirmPopupGroup!: Phaser.GameObjects.Container;
  private createTablePopupGroup!: Phaser.GameObjects.Container;
  
  // Main menu buttons (for enabling/disabling)
  private createTableBtn!: Phaser.GameObjects.Image;
  private joinTableBtn!: Phaser.GameObjects.Image;
  private settingsButton!: Phaser.GameObjects.Image;
  private settingsPanelGroup!: Phaser.GameObjects.Container;
  private sfxSlider?: VolumeSlider;
  private musicSlider?: VolumeSlider;
  private deckButtons?: { classic: Phaser.GameObjects.Image; alternative: Phaser.GameObjects.Image };
  private deckButtonLabels?: { classic: Phaser.GameObjects.Text; alternative: Phaser.GameObjects.Text };
  private tableButtons?: { green: Phaser.GameObjects.Image; blue: Phaser.GameObjects.Image; red: Phaser.GameObjects.Image };
  private tableButtonLabels?: { green: Phaser.GameObjects.Text; blue: Phaser.GameObjects.Text; red: Phaser.GameObjects.Text };
  
  // Table list HTML element
  private tableListContainer: HTMLDivElement | null = null;
  
  // Create table form HTML element
  private createTableFormContainer: HTMLDivElement | null = null;

  // Guild selection HTML element
  private guildSelectionContainer: HTMLDivElement | null = null;
  private guildSelectionOptions: HoldemGuildOption[] = [];
  private guildSelectionInProgress = false;
  
  // Pending join action
  private pendingJoinTable: PokerTableState | null = null;

  constructor() {
    super({ key: 'MenuScene' });
  }

  preload(): void {
    this.load.image('background', 'assets/background_casino.webp');
    this.load.image('button1', 'assets/button_1.webp');
    this.load.image('button2', 'assets/button_2.webp');
    this.load.image('button_small', 'assets/button_1_small.webp');
    this.load.image('settings_button', 'assets/settingsButton_asset.webp');
    this.load.image('table_bg', 'assets/table_bg_asset.webp');
    this.load.image('pc_acc', 'assets/pc_acc_asset_new.webp');
    AVATAR_PRESET_KEYS.forEach((key) => {
      this.load.image(key, `assets/Avatars/${key.replace('avatar', '')}.webp`);
    });
  }

  create(): void {
    this.events.on('shutdown', () => {
      this.unsubscribeAudioSettings?.();
      this.unsubscribeAudioSettings = undefined;
    });

    this.unsubscribeAudioSettings = onAudioSettingsChanged((settings) => {
      this.audioSettings = settings;
      this.sfxSlider?.updateFromValue(settings.sfxVolume);
      this.musicSlider?.updateFromValue(settings.musicVolume);
    });
    this.deckSettings = loadDeckSettings();

    // Black background
    this.cameras.main.setBackgroundColor('#000000');

    // Background image - centered, fills 1920x1080
    const bg = this.add.image(960, 540, 'background');
    bg.setDisplaySize(1920, 1080);

    // Create main menu container
    this.mainMenuGroup = this.add.container(0, 0);
    
    // Buttons - smaller (60% scale), one under another, centered
    const btn1 = this.add.image(960, 570, 'button1').setScale(0.6);
    const btn2 = this.add.image(960, 690, 'button2').setScale(0.6);
    
    // Store references for later enabling
    this.createTableBtn = btn1;
    this.joinTableBtn = btn2;
    
    // Initially disable buttons (greyed out) - will be enabled after login
    btn1.setTint(0x666666);
    btn2.setTint(0x666666);

    // Button text - gothic font, centered on buttons
    const btn1Text = this.add.text(960, 570, 'STWÓRZ STÓŁ', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const btn2Text = this.add.text(960, 690, 'DOŁĄCZ', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    // Add to main menu group
    this.mainMenuGroup.add([btn1, btn2, btn1Text, btn2Text]);

    // Button actions (will only work when interactive is set)
    btn1.on('pointerdown', () => this.showCreateTablePopup());
    btn2.on('pointerdown', () => this.showTableListView());

    // Hover effects (only visible when buttons are interactive)
    btn1.on('pointerover', () => { if (this.authenticated) btn1.setTint(0xcccccc); });
    btn1.on('pointerout', () => { if (this.authenticated) btn1.clearTint(); });
    btn2.on('pointerover', () => { if (this.authenticated) btn2.setTint(0xcccccc); });
    btn2.on('pointerout', () => { if (this.authenticated) btn2.clearTint(); });

    // Create table list view (initially hidden)
    this.createTableListView();
    
    // Create confirm popup (initially hidden)
    this.createConfirmPopup();
    
    // Create table creation popup (initially hidden)
    this.createCreateTablePopup();

    // Connection status (at top, hidden by default)
    this.connectionStatus = this.add.text(640, 50, '', {
      fontSize: '16px',
      color: '#1a6b8a',
      fontFamily: 'Rajdhani, Arial, sans-serif',
    }).setOrigin(0.5).setVisible(false);

    this.statusText = this.add.text(640, 75, '', {
      fontSize: '14px',
      color: '#d4d4d4',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5).setVisible(false);

    // Create Discord login button
    this.createDiscordLoginButton();

    // Create settings button + panel
    this.createSettingsButton();
    this.createSettingsPanel();
    this.layoutTopButtons();

    // Create PC balance display (top-left corner)
    this.createPCBalanceDisplay();

    // Setup client events
    this.setupClientEvents();

    // Auto-connect with session after 500ms
    this.time.delayedCall(500, () => this.autoConnect());
  }

  private createConfirmPopup(): void {
    this.confirmPopupGroup = this.add.container(0, 0);
    this.confirmPopupGroup.setVisible(false);
    this.confirmPopupGroup.setDepth(1000);
    
    // Dark overlay
    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.7);
    overlay.setInteractive();
    
    // Popup background
    const popupBg = this.add.rectangle(960, 540, 600, 250, 0x0d1530);
    popupBg.setStrokeStyle(2, 0xd4af37);
    
    // Title
    const popupTitle = this.add.text(960, 470, 'ZMIANA STOŁU', {
      fontSize: '28px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Message
    const popupMessage = this.add.text(960, 520, 'Jesteś już przy innym stole.\nCzy chcesz go opuścić i dołączyć do nowego?', {
      fontSize: '18px',
      color: '#cccccc',
      fontFamily: 'Share Tech Mono, monospace',
      align: 'center',
    }).setOrigin(0.5);
    
    // Confirm button
    const confirmBtn = this.add.image(850, 600, 'button_small').setInteractive().setScale(0.5);
    const confirmBtnText = this.add.text(850, 600, 'TAK', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Cancel button
    const cancelBtn = this.add.image(1070, 600, 'button_small').setInteractive().setScale(0.5);
    const cancelBtnText = this.add.text(1070, 600, 'NIE', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Hover effects
    confirmBtn.on('pointerover', () => confirmBtn.setTint(0xcccccc));
    confirmBtn.on('pointerout', () => confirmBtn.clearTint());
    cancelBtn.on('pointerover', () => cancelBtn.setTint(0xcccccc));
    cancelBtn.on('pointerout', () => cancelBtn.clearTint());
    
    // Button actions
    confirmBtn.on('pointerdown', () => this.confirmLeaveAndJoin());
    cancelBtn.on('pointerdown', () => this.hideConfirmPopup());
    
    this.confirmPopupGroup.add([overlay, popupBg, popupTitle, popupMessage, confirmBtn, confirmBtnText, cancelBtn, cancelBtnText]);
  }

  private createCreateTablePopup(): void {
    this.createTablePopupGroup = this.add.container(0, 0);
    this.createTablePopupGroup.setVisible(false);
    this.createTablePopupGroup.setDepth(1000);
    
    // Dark overlay
    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.7);
    overlay.setInteractive();
    
    // Popup background - larger to fit form
    const popupBg = this.add.rectangle(960, 540, 700, 550, 0x0d1530);
    popupBg.setStrokeStyle(2, 0xd4af37);
    
    // Title
    const popupTitle = this.add.text(960, 300, 'UTWÓRZ NOWY STÓŁ', {
      fontSize: '28px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Create button
    const createBtn = this.add.image(850, 780, 'button_small').setInteractive().setScale(0.5);
    const createBtnText = this.add.text(850, 780, 'UTWÓRZ', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Cancel button
    const cancelBtn = this.add.image(1070, 780, 'button_small').setInteractive().setScale(0.5);
    const cancelBtnText = this.add.text(1070, 780, 'ANULUJ', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Hover effects
    createBtn.on('pointerover', () => createBtn.setTint(0xcccccc));
    createBtn.on('pointerout', () => createBtn.clearTint());
    cancelBtn.on('pointerover', () => cancelBtn.setTint(0xcccccc));
    cancelBtn.on('pointerout', () => cancelBtn.clearTint());
    
    // Button actions
    createBtn.on('pointerdown', () => this.submitCreateTable());
    cancelBtn.on('pointerdown', () => this.hideCreateTablePopup());
    
    this.createTablePopupGroup.add([overlay, popupBg, popupTitle, createBtn, createBtnText, cancelBtn, cancelBtnText]);
  }

  private showCreateTablePopup(): void {
    if (!this.authenticated) {
      this.statusText.setText('Najpierw połącz z serwerem').setColor('#ff6b6b').setVisible(true);
      return;
    }
    
    this.createTablePopupGroup.setVisible(true);
    this.createCreateTableForm();
  }

  private hideCreateTablePopup(): void {
    this.createTablePopupGroup.setVisible(false);
    
    // Remove HTML form
    if (this.createTableFormContainer) {
      if ((this.createTableFormContainer as any)._cleanup) {
        (this.createTableFormContainer as any)._cleanup();
      }
      this.createTableFormContainer.remove();
      this.createTableFormContainer = null;
    }
  }

  private createCreateTableForm(): void {
    // Remove existing if present
    if (this.createTableFormContainer) {
      this.createTableFormContainer.remove();
    }
    
    const container = document.createElement('div');
    container.id = 'create-table-form';
    
    // Get game canvas for positioning
    const gameCanvas = document.querySelector('#game-container canvas') as HTMLCanvasElement;
    
    // Position relative to game container
    container.style.position = 'absolute';
    container.style.pointerEvents = 'auto';
    
    const baseFormWidth = 600;
    const baseTopOffset = 330;

    const updatePosition = () => {
      if (!gameCanvas) return;
      
      const canvasRect = gameCanvas.getBoundingClientRect();
      const scaleX = canvasRect.width / 1920;
      const scaleY = canvasRect.height / 1080;
      const scale = Math.min(scaleX, scaleY);
      
      const formWidth = baseFormWidth * scale;
      const formTop = canvasRect.top + (baseTopOffset * scale);
      
      container.style.left = `${canvasRect.left + (canvasRect.width / 2) - (formWidth / 2)}px`;
      container.style.top = `${formTop}px`;
      container.style.width = `${baseFormWidth}px`;
      container.style.transform = `scale(${scale})`;
      container.style.transformOrigin = 'top left';
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    (container as any)._cleanup = () => window.removeEventListener('resize', updatePosition);
    
    container.style.padding = '20px';
    container.style.backgroundColor = 'transparent';
    
    // Form grid - 2 columns
    const formGrid = document.createElement('div');
    formGrid.style.display = 'grid';
    formGrid.style.gridTemplateColumns = '1fr 1fr';
    formGrid.style.gap = '15px';
    formGrid.style.marginBottom = '10px';
    
    // Table Name - full width
    const nameGroup = this.createFormField('tableName', 'NAZWA STOŁU', 'text', `Stół Pokera`, true);
    nameGroup.style.gridColumn = '1 / -1';
    formGrid.appendChild(nameGroup);
    
    // Max Players
    formGrid.appendChild(this.createFormField('maxPlayers', 'MAX GRACZY', 'number', '8'));
    
    // Empty space for alignment
    const spacer = document.createElement('div');
    formGrid.appendChild(spacer);
    
    // Blinds row - use server config if available
    const defaultSmallBlind = this.serverConfig?.smallBlind?.toString() || '5';
    const defaultBigBlind = this.serverConfig?.bigBlind?.toString() || '10';
    formGrid.appendChild(this.createFormField('smallBlind', 'MAŁY BLIND', 'number', defaultSmallBlind));
    formGrid.appendChild(this.createFormField('bigBlind', 'DUŻY BLIND', 'number', defaultBigBlind));
    
    // Buyin row - use server config if available
    const defaultMinBuyin = this.serverConfig?.minBuyin?.toString() || '100';
    const defaultMaxBuyin = this.serverConfig?.maxBuyin?.toString() || '200';
    formGrid.appendChild(this.createFormField('minBuyin', 'MIN BUY-IN', 'number', defaultMinBuyin));
    formGrid.appendChild(this.createFormField('maxBuyin', 'MAX BUY-IN', 'number', defaultMaxBuyin));

    const loosePlayGroup = this.createCheckboxField(
      'loosePlay',
      'LUŹNA GRA',
      'Brak połączenia z systemem ekonomii (brak komunikacji z API).',
    );
    loosePlayGroup.style.gridColumn = '1 / -1';
    formGrid.appendChild(loosePlayGroup);
    
    container.appendChild(formGrid);
    
    this.createTableFormContainer = container;
    document.body.appendChild(container);
  }

  private createFormField(id: string, label: string, type: string, defaultValue: string, fullWidth = false): HTMLDivElement {
    const group = document.createElement('div');
    
    const labelEl = document.createElement('label');
    labelEl.innerText = label;
    labelEl.style.display = 'block';
    labelEl.style.color = '#d4af37';
    labelEl.style.fontSize = '12px';
    labelEl.style.fontFamily = 'Rajdhani, Arial, sans-serif';
    labelEl.style.fontWeight = 'normal';
    labelEl.style.textTransform = 'uppercase';
    labelEl.style.marginBottom = '5px';
    
    const input = document.createElement('input');
    input.type = type;
    input.id = `create-table-${id}`;
    input.value = defaultValue;
    input.style.width = '100%';
    input.style.padding = '10px';
    input.style.borderRadius = '4px';
    input.style.border = '1px solid #444';
    input.style.backgroundColor = '#050814';
    input.style.color = '#ccc';
    input.style.fontSize = '14px';
    input.style.fontFamily = 'Share Tech Mono, monospace';
    input.style.boxSizing = 'border-box';
    
    if (type === 'number') {
      input.min = '1';
      if (id === 'maxPlayers') {
        input.min = '2';
        input.max = '9';
      }
    }
    
    group.appendChild(labelEl);
    group.appendChild(input);
    
    return group;
  }

  private createCheckboxField(id: string, label: string, helperText?: string): HTMLDivElement {
    const group = document.createElement('div');
    group.style.display = 'flex';
    group.style.alignItems = 'center';
    group.style.gap = '10px';
    group.style.padding = '6px 10px';
    group.style.border = '1px solid #333';
    group.style.borderRadius = '4px';
    group.style.backgroundColor = '#050814';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = `create-table-${id}`;
    checkbox.style.width = '16px';
    checkbox.style.height = '16px';
    checkbox.style.cursor = 'pointer';

    const textWrap = document.createElement('div');

    const labelEl = document.createElement('label');
    labelEl.innerText = label;
    labelEl.htmlFor = checkbox.id;
    labelEl.style.display = 'block';
    labelEl.style.color = '#d4af37';
    labelEl.style.fontSize = '12px';
    labelEl.style.fontFamily = 'Rajdhani, Arial, sans-serif';
    labelEl.style.fontWeight = 'normal';
    labelEl.style.textTransform = 'uppercase';
    labelEl.style.cursor = 'pointer';

    textWrap.appendChild(labelEl);

    if (helperText) {
      const helperEl = document.createElement('div');
      helperEl.innerText = helperText;
      helperEl.style.color = '#888888';
      helperEl.style.fontSize = '11px';
      helperEl.style.fontFamily = 'Share Tech Mono, monospace';
      textWrap.appendChild(helperEl);
    }

    group.appendChild(checkbox);
    group.appendChild(textWrap);

    return group;
  }

  private async submitCreateTable(): Promise<void> {
    const tableNameInput = document.getElementById('create-table-tableName') as HTMLInputElement;
    const tableName = tableNameInput?.value?.trim() || `Stół Pokera`;
    const maxPlayers = parseInt((document.getElementById('create-table-maxPlayers') as HTMLInputElement)?.value) || 8;
    const smallBlind = parseInt((document.getElementById('create-table-smallBlind') as HTMLInputElement)?.value) || 1;
    const bigBlind = parseInt((document.getElementById('create-table-bigBlind') as HTMLInputElement)?.value) || 2;
    const minBuyin = parseInt((document.getElementById('create-table-minBuyin') as HTMLInputElement)?.value) || 100;
    const maxBuyin = parseInt((document.getElementById('create-table-maxBuyin') as HTMLInputElement)?.value) || 10000;
    const loosePlay = (document.getElementById('create-table-loosePlay') as HTMLInputElement)?.checked || false;
    
    console.log('[CreateTable] tableName:', tableName, 'from input:', tableNameInput?.value);
    
    // Validation
    if (smallBlind >= bigBlind) {
      this.statusText.setText('Mały blind musi być mniejszy od dużego!').setColor('#ff6b6b').setVisible(true);
      return;
    }
    
    if (minBuyin >= maxBuyin) {
      this.statusText.setText('Min buy-in musi być mniejszy od max!').setColor('#ff6b6b').setVisible(true);
      return;
    }
    
    if (maxPlayers < 2 || maxPlayers > 9) {
      this.statusText.setText('Liczba graczy: 2-9').setColor('#ff6b6b').setVisible(true);
      return;
    }
    
    this.hideCreateTablePopup();
    await this.createAndJoinTable({
      name: tableName,
      maxPlayers,
      smallBlind,
      bigBlind,
      minBuyin,
      maxBuyin,
      loosePlay,
    });
  }

  private showConfirmPopup(table: PokerTableState): void {
    this.pendingJoinTable = table;
    this.confirmPopupGroup.setVisible(true);
    
    // Hide HTML table so popup is visible above it
    if (this.tableListContainer) {
      this.tableListContainer.style.display = 'none';
    }
  }

  private hideConfirmPopup(): void {
    this.pendingJoinTable = null;
    this.confirmPopupGroup.setVisible(false);
    
    // Show HTML table again
    if (this.tableListContainer) {
      this.tableListContainer.style.display = 'block';
    }
  }

  private async confirmLeaveAndJoin(): Promise<void> {
    if (!this.pendingJoinTable) return;
    
    const table = this.pendingJoinTable;
    this.hideConfirmPopup();
    
    this.statusText.setText('Opuszczanie stołu...').setColor('#ffd93d').setVisible(true);
    
    // Leave current table
    const result = await pokerClient.leaveTable();
    if (!result.success) {
      this.statusText
        .setText('Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
        .setColor('#ff6b6b').setVisible(true);
      return;
    }
    
    // Join new table
    await this.doJoinTable(table);
  }

  // Auth button references
  private authButton!: Phaser.GameObjects.Image;
  private authButtonText!: Phaser.GameObjects.Text;
  private authButtonGroup!: Phaser.GameObjects.Container;

  private createDiscordLoginButton(): void {
    // Position: top-right corner with 20px margin (game coords: 1920-20, 20)
    // Button1 asset at 0.4 scale is about 150px wide, so center it at 1920 - 20 - 75 = 1825
    const btnX = 1920 - 20 - 75;
    const btnY = 20 + 25; // 20px margin + half button height

    this.authButtonGroup = this.add.container(0, 0);
    this.authButtonGroup.setDepth(100); // Above other elements

    // Login button (using small button asset)
    this.authButton = this.add.image(btnX, btnY, 'button_small').setScale(0.4);
    this.authButton.setInteractive({ useHandCursor: true });

    // Button text - same font as other buttons
    this.authButtonText = this.add.text(btnX, btnY, 'ZALOGUJ', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    // Hover effects
    this.authButton.on('pointerover', () => this.authButton.setTint(0xcccccc));
    this.authButton.on('pointerout', () => this.authButton.clearTint());

    // Click action - will be updated based on auth state
    this.authButton.on('pointerdown', () => this.handleAuthButtonClick());

    this.authButtonGroup.add([this.authButton, this.authButtonText]);
  }

  private handleAuthButtonClick(): void {
    if (this.authenticated || this.authSessionActive) {
      // Logout
      window.location.href = buildHoldemBackendUrl('/logout');
    } else {
      // Login with Discord
      const returnTo = encodeURIComponent(window.location.origin);
      window.location.href = buildHoldemBackendUrl(`/auth/discord?return_to=${returnTo}`);
    }
  }

  private updateAuthButton(): void {
    if (this.authenticated || this.authSessionActive) {
      this.authButtonText.setText('WYLOGUJ');
      this.authButton.setTexture('button_small');
    } else {
      this.authButtonText.setText('ZALOGUJ');
      this.authButton.setTexture('button_small');
    }
    this.layoutTopButtons();
  }

  private layoutTopButtons(): void {
    if (!this.authButton || !this.authButtonText || !this.settingsButton) {
      return;
    }

    const margin = 20;
    const gap = 15;
    const canvasWidth = this.scale?.width || 1920;
    const authHeight = this.authButton.displayHeight || 50;

    // Match settings button height to auth button
    this.settingsButton.setDisplaySize(authHeight, authHeight);

    const settingsWidth = this.settingsButton.displayWidth;
    const authWidth = this.authButton.displayWidth;
    const topY = margin + authHeight / 2;

    const settingsX = canvasWidth - margin - (settingsWidth / 2);
    const authX = settingsX - (settingsWidth / 2) - gap - (authWidth / 2);

    this.settingsButton.setPosition(settingsX, topY);
    this.authButton.setPosition(authX, topY);
    this.authButtonText.setPosition(authX, topY);
  }

  private createSettingsButton(): void {
    this.settingsButton = this.add.image(0, 0, 'settings_button');
    this.settingsButton.setDepth(101);
    this.settingsButton.setInteractive({ useHandCursor: true });

    // Align initial size with auth button; layout will adjust again
    if (this.authButton?.displayHeight) {
      this.settingsButton.setDisplaySize(this.authButton.displayHeight, this.authButton.displayHeight);
    }

    this.settingsButton.on('pointerover', () => this.settingsButton.setTint(0xcccccc));
    this.settingsButton.on('pointerout', () => this.settingsButton.clearTint());
    this.settingsButton.on('pointerdown', () => this.showSettingsPanel());
  }

  private createSettingsPanel(): void {
    this.settingsPanelGroup = this.add.container(0, 0);
    this.settingsPanelGroup.setDepth(1000);
    this.settingsPanelGroup.setVisible(false);

    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.65);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.hideSettingsPanel());

    const panelCenterX = 960;
    const titleY = 300;
    const subtitleY = 335;
    const deckLabelY = 375;
    const deckButtonsY = 420;
    const tableLabelY = 460;
    const tableButtonsY = 505;
    const avatarLabelY = 555;
    const avatarRow1Y = 605;
    const avatarRow2Y = 685;
    const sfxSliderY = 760;
    const musicSliderY = 830;
    const closeButtonY = 900;

    const panelBg = this.add.rectangle(960, 540, 640, 800, 0x050814, 0.95);
    panelBg.setStrokeStyle(2, 0xd4af37);

    const title = this.add.text(panelCenterX, titleY, 'USTAWIENIA', {
      fontSize: '30px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);

    const subtitle = this.add.text(panelCenterX, subtitleY, 'Dostosuj głośność, talię, stół i avatar', {
      fontSize: '16px',
      color: '#cccccc',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5);

    const deckLabel = this.add.text(panelCenterX, deckLabelY, 'TALIA', {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const classicBtn = this.add.image(820, deckButtonsY, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const altBtn = this.add.image(1100, deckButtonsY, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const classicText = this.add.text(820, deckButtonsY, 'KLASYCZNA', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    const altText = this.add.text(1100, deckButtonsY, 'ALTERNATYWNA', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    classicBtn.on('pointerover', () => classicBtn.setTint(0xcccccc));
    classicBtn.on('pointerout', () => classicBtn.clearTint());
    classicBtn.on('pointerdown', () => this.setDeck('classic'));

    altBtn.on('pointerover', () => altBtn.setTint(0xcccccc));
    altBtn.on('pointerout', () => altBtn.clearTint());
    altBtn.on('pointerdown', () => this.setDeck('alternative'));

    this.deckButtons = { classic: classicBtn, alternative: altBtn };
    this.deckButtonLabels = { classic: classicText, alternative: altText };
    this.updateDeckButtons();

    const tableLabel = this.add.text(panelCenterX, tableLabelY, 'STÓŁ', {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const tableGreenBtn = this.add.image(730, tableButtonsY, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const tableBlueBtn = this.add.image(960, tableButtonsY, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const tableRedBtn = this.add.image(1190, tableButtonsY, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const tableGreenText = this.add.text(730, tableButtonsY, 'ZIELONY', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    const tableBlueText = this.add.text(960, tableButtonsY, 'NIEBIESKI', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    const tableRedText = this.add.text(1190, tableButtonsY, 'CZERWONY', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    tableGreenBtn.on('pointerover', () => tableGreenBtn.setTint(0xcccccc));
    tableGreenBtn.on('pointerout', () => tableGreenBtn.clearTint());
    tableGreenBtn.on('pointerdown', () => this.setTableTheme('green'));

    tableBlueBtn.on('pointerover', () => tableBlueBtn.setTint(0xcccccc));
    tableBlueBtn.on('pointerout', () => tableBlueBtn.clearTint());
    tableBlueBtn.on('pointerdown', () => this.setTableTheme('blue'));

    tableRedBtn.on('pointerover', () => tableRedBtn.setTint(0xcccccc));
    tableRedBtn.on('pointerout', () => tableRedBtn.clearTint());
    tableRedBtn.on('pointerdown', () => this.setTableTheme('red'));

    this.tableButtons = { green: tableGreenBtn, blue: tableBlueBtn, red: tableRedBtn };
    this.tableButtonLabels = { green: tableGreenText, blue: tableBlueText, red: tableRedText };
    this.updateTableButtons();

    const avatarLabel = this.add.text(panelCenterX, avatarLabelY, 'AVATAR', {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    this.avatarOptions = [];
    this.discordAvatarOption = null;
    const avatarOptionIds = ['discord', ...AVATAR_PRESET_KEYS];
    const avatarColumns = 5;
    const avatarFrameSize = 72;
    const avatarGap = 14;
    const avatarStartX = panelCenterX - ((avatarColumns - 1) * (avatarFrameSize + avatarGap)) / 2;
    const avatarRows = [avatarRow1Y, avatarRow2Y];

    avatarOptionIds.forEach((id, index) => {
      const row = Math.floor(index / avatarColumns);
      const col = index % avatarColumns;
      const option = this.createAvatarOption(
        avatarStartX + col * (avatarFrameSize + avatarGap),
        avatarRows[row],
        id,
        avatarFrameSize,
      );
      this.avatarOptions.push(option);
      if (id === 'discord') {
        this.discordAvatarOption = option;
      }
    });

    if (this.avatarSettings.discordAvatarUrl) {
      this.ensureDiscordAvatarTexture(this.avatarSettings.discordAvatarUrl);
    }
    this.updateAvatarOptionStyles();

    this.sfxSlider = this.createVolumeSlider(panelCenterX, sfxSliderY, 'SFX', this.audioSettings.sfxVolume, (value) => this.setSfxVolume(value));
    this.musicSlider = this.createVolumeSlider(panelCenterX, musicSliderY, 'MUZYKA', this.audioSettings.musicVolume, (value) => this.setMusicVolume(value));

    const closeBtn = this.add.image(panelCenterX, closeButtonY, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const closeText = this.add.text(panelCenterX, closeButtonY, 'ZAMKNIJ', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    closeBtn.on('pointerover', () => closeBtn.setTint(0xcccccc));
    closeBtn.on('pointerout', () => closeBtn.clearTint());
    closeBtn.on('pointerdown', () => this.hideSettingsPanel());

    this.settingsPanelGroup.add([
      overlay,
      panelBg,
      title,
      subtitle,
      deckLabel,
      classicBtn,
      altBtn,
      classicText,
      altText,
      tableLabel,
      tableGreenBtn,
      tableBlueBtn,
      tableRedBtn,
      tableGreenText,
      tableBlueText,
      tableRedText,
      avatarLabel,
      ...this.avatarOptions.map(option => option.container),
      this.sfxSlider.container,
      this.musicSlider.container,
      closeBtn,
      closeText,
    ]);
  }

  private createAvatarOption(
    x: number,
    y: number,
    id: string,
    frameSize: number,
  ): AvatarOption {
    const imageSize = frameSize - 8;
    const frame = this.add.rectangle(0, 0, frameSize, frameSize, 0x000000, 0.35);
    frame.setStrokeStyle(2, 0x444444);

    const textureKey = id === 'discord' ? AVATAR_PRESET_KEYS[0] : id;
    const image = this.add.image(0, 0, textureKey);
    image.setDisplaySize(imageSize, imageSize);

    let placeholder: Phaser.GameObjects.Container | undefined;
    if (id === 'discord') {
      image.setVisible(false);
      const placeholderBg = this.add.rectangle(0, 0, imageSize, imageSize, 0x050814, 0.9);
      const placeholderText = this.add.text(0, 0, 'DC', {
        fontSize: '18px',
        color: '#d4af37',
        fontFamily: 'Rajdhani, Arial, sans-serif',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      placeholder = this.add.container(0, 0, [placeholderBg, placeholderText]);
    }

    const container = this.add.container(x, y, placeholder ? [frame, placeholder, image] : [frame, image]);
    container.setSize(frameSize, frameSize);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      if (this.avatarSettings.selectedAvatarId !== id) {
        frame.setStrokeStyle(2, 0xcccccc);
      }
    });
    container.on('pointerout', () => this.updateAvatarOptionStyles());
    container.on('pointerdown', () => this.setAvatarSelection(id));

    return { id, container, frame, image, placeholder };
  }

  private updateAvatarOptionStyles(): void {
    const selectedId = this.avatarSettings.selectedAvatarId;
    const hasDiscordAvatar = !!this.avatarSettings.discordAvatarUrl;

    this.avatarOptions.forEach((option) => {
      const isSelected = selectedId === option.id;
      option.frame.setStrokeStyle(2, isSelected ? 0xd4af37 : 0x444444);

      if (option.id === 'discord') {
        const showImage = hasDiscordAvatar && this.discordAvatarTextureKey !== null && this.textures.exists(this.discordAvatarTextureKey);
        option.image.setVisible(showImage);
        option.placeholder?.setVisible(!showImage);
        option.image.setAlpha(hasDiscordAvatar ? 1 : 0.4);
        option.placeholder?.setAlpha(hasDiscordAvatar ? 1 : 0.6);
      } else {
        option.image.setAlpha(1);
      }
    });
  }

  private setAvatarSelection(avatarId: string): void {
    this.avatarSettings = {
      ...this.avatarSettings,
      selectedAvatarId: avatarId,
    };
    saveAvatarSettings(this.avatarSettings);
    this.updateAvatarOptionStyles();
  }

  private ensureDiscordAvatarTexture(url: string): void {
    const key = this.buildAvatarTextureKey(url, 'discord_avatar');
    this.discordAvatarTextureKey = key;

    if (this.textures.exists(key)) {
      this.applyDiscordAvatarTexture(key);
      return;
    }

    this.load.image(key, url);
    this.load.once(`filecomplete-image-${key}`, () => this.applyDiscordAvatarTexture(key));
    if (!this.load.isLoading()) {
      this.load.start();
    }
  }

  private applyDiscordAvatarTexture(key: string): void {
    if (!this.discordAvatarOption) return;
    this.discordAvatarOption.image.setTexture(key);
    this.discordAvatarOption.image.setVisible(true);
    this.discordAvatarOption.placeholder?.setVisible(false);
    this.updateAvatarOptionStyles();
  }

  private buildAvatarTextureKey(value: string, prefix: string): string {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (hash * 31 + value.charCodeAt(i)) | 0;
    }
    const safeHash = Math.abs(hash);
    return `${prefix}_${safeHash}`;
  }

  private createVolumeSlider(
    x: number,
    y: number,
    label: string,
    initialValue: number,
    onChange: (value: number) => void,
  ): VolumeSlider {
    const sliderWidth = 320;
    const trackX = -(sliderWidth / 2);
    const container = this.add.container(x, y);

    const labelText = this.add.text(0, -30, label, {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const track = this.add.rectangle(trackX, 8, sliderWidth, 8, 0x222222, 0.9)
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });

    const fill = this.add.rectangle(trackX, 8, sliderWidth * initialValue, 8, 0xd4af37, 1)
      .setOrigin(0, 0.5);

    const handle = this.add.circle(trackX + (sliderWidth * initialValue), 8, 12, 0xd4af37)
      .setInteractive({ useHandCursor: true });

    const valueText = this.add.text(trackX + sliderWidth + 35, 8, `${Math.round(initialValue * 100)}%`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5);

    container.add([labelText, track, fill, handle, valueText]);

    const applyValue = (value: number) => {
      const clamped = Phaser.Math.Clamp(value, 0, 1);
      fill.width = sliderWidth * clamped;
      handle.x = trackX + (sliderWidth * clamped);
      valueText.setText(`${Math.round(clamped * 100)}%`);
    };

    const worldXFromPointer = (p: Phaser.Input.Pointer): number => (p.worldX ?? p.x ?? 0);

    const updateFromPointer = (pointer: Phaser.Input.Pointer) => {
      const bounds = track.getBounds();
      const worldX = worldXFromPointer(pointer);
      const localX = Phaser.Math.Clamp(worldX - bounds.x, 0, bounds.width);
      const value = localX / bounds.width;
      applyValue(value);
      onChange(value);
    };

    track.on('pointerdown', (pointer: Phaser.Input.Pointer) => updateFromPointer(pointer));
    this.input.setDraggable(handle);
    handle.on('drag', (pointer: Phaser.Input.Pointer) => updateFromPointer(pointer));
    handle.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      updateFromPointer(pointer);
    });
    handle.on('pointerover', () => handle.setScale(1.05));
    handle.on('pointerout', () => handle.setScale(1));

    return {
      container,
      updateFromValue: applyValue,
    };
  }

  private showSettingsPanel(): void {
    if (!this.settingsPanelGroup) return;
    this.settingsPanelGroup.setVisible(true);
    this.avatarSettings = loadAvatarSettings();
    if (this.avatarSettings.discordAvatarUrl) {
      this.ensureDiscordAvatarTexture(this.avatarSettings.discordAvatarUrl);
    }
    this.updateAvatarOptionStyles();
    this.sfxSlider?.updateFromValue(this.audioSettings.sfxVolume);
    this.musicSlider?.updateFromValue(this.audioSettings.musicVolume);
    this.updateDeckButtons();
    this.updateTableButtons();
  }

  private hideSettingsPanel(): void {
    if (!this.settingsPanelGroup) return;
    this.settingsPanelGroup.setVisible(false);
  }

  private updateDeckButtons(): void {
    if (!this.deckButtons || !this.deckButtonLabels) return;
    const isClassic = this.deckSettings.selectedDeck === 'classic';
    this.deckButtons.classic.setTexture('button_small');
    this.deckButtons.alternative.setTexture('button_small');
    this.deckButtonLabels.classic.setColor(isClassic ? '#d4af37' : '#ffffff');
    this.deckButtonLabels.alternative.setColor(isClassic ? '#ffffff' : '#d4af37');
  }

  private updateTableButtons(): void {
    if (!this.tableButtons || !this.tableButtonLabels) return;
    const selectedTheme = this.deckSettings.tableTheme;
    this.tableButtons.green.setTexture('button_small');
    this.tableButtons.blue.setTexture('button_small');
    this.tableButtons.red.setTexture('button_small');
    this.tableButtonLabels.green.setColor(selectedTheme === 'green' ? '#ffffff' : '#d4af37');
    this.tableButtonLabels.blue.setColor(selectedTheme === 'blue' ? '#ffffff' : '#d4af37');
    this.tableButtonLabels.red.setColor(selectedTheme === 'red' ? '#ffffff' : '#d4af37');
  }

  private setDeck(deckId: DeckId): void {
    if (this.deckSettings.selectedDeck === deckId) return;
    this.deckSettings.selectedDeck = deckId;
    saveDeckSettings(this.deckSettings);
    this.updateDeckButtons();
  }

  private setTableTheme(theme: TableTheme): void {
    if (this.deckSettings.tableTheme === theme) return;
    this.deckSettings.tableTheme = theme;
    saveDeckSettings(this.deckSettings);
    this.updateTableButtons();
  }

  private setSfxVolume(value: number): void {
    this.audioSettings.sfxVolume = Phaser.Math.Clamp(value, 0, 1);
    saveAudioSettings(this.audioSettings);
  }

  private setMusicVolume(value: number): void {
    this.audioSettings.musicVolume = Phaser.Math.Clamp(value, 0, 1);
    saveAudioSettings(this.audioSettings);
  }

  private createTableListView(): void {
    // Create Phaser container for table list buttons
    this.tableListGroup = this.add.container(0, 0);
    this.tableListGroup.setVisible(false);
    
    // Title (below logo with margin)
    const title = this.add.text(960, 300, 'DOSTĘPNE STOŁY', {
      fontSize: '32px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);
    
    // Join button (bottom)
    const joinBtn = this.add.image(960, 800, 'button_small').setInteractive().setScale(0.6);
    const joinBtnText = this.add.text(960, 800, 'DOŁĄCZ DO STOŁU', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Back button
    const backBtn = this.add.image(960, 900, 'button_small').setInteractive().setScale(0.6);
    const backBtnText = this.add.text(960, 900, 'WRÓĆ', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Hover effects
    joinBtn.on('pointerover', () => joinBtn.setTint(0xcccccc));
    joinBtn.on('pointerout', () => joinBtn.clearTint());
    backBtn.on('pointerover', () => backBtn.setTint(0xcccccc));
    backBtn.on('pointerout', () => backBtn.clearTint());
    
    // Button actions
    joinBtn.on('pointerdown', () => this.joinSelectedTable());
    backBtn.on('pointerdown', () => this.hideTableListView());
    
    this.tableListGroup.add([title, joinBtn, joinBtnText, backBtn, backBtnText]);
  }

  private createTableListHTML(): void {
    // Remove existing if present
    if (this.tableListContainer) {
      this.tableListContainer.remove();
    }
    
    const container = document.createElement('div');
    container.id = 'table-list-container';
    
    // Get game canvas for positioning
    const gameCanvas = document.querySelector('#game-container canvas') as HTMLCanvasElement;
    const gameContainer = document.getElementById('game-container');
    
    // Position relative to game container
    container.style.position = 'absolute';
    container.style.pointerEvents = 'auto';
    
    // Calculate scale and position based on canvas
    const updatePosition = () => {
      if (!gameCanvas || !gameContainer) return;
      
      const canvasRect = gameCanvas.getBoundingClientRect();
      const scaleX = canvasRect.width / 1920;
      const scaleY = canvasRect.height / 1080;
      const scale = Math.min(scaleX, scaleY);
      
      // Table positioned at center (game coords: 960, 540), scaled
      const tableWidth = 800 * scale;
      const tableHeight = 350 * scale;
      
      container.style.left = `${canvasRect.left + (canvasRect.width / 2) - (tableWidth / 2)}px`;
      container.style.top = `${canvasRect.top + (canvasRect.height * 0.38)}px`;
      container.style.width = `${tableWidth}px`;
      container.style.maxHeight = `${tableHeight}px`;
      container.style.fontSize = `${14 * scale}px`;
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    
    // Store cleanup function
    (container as any)._cleanup = () => window.removeEventListener('resize', updatePosition);
    
    container.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
    container.style.borderRadius = '8px';
    container.style.border = '2px solid #d4af37';
    container.style.boxShadow = '0 0 30px rgba(212, 175, 55, 0.3)';
    container.style.overflow = 'hidden';
    
    // Table element
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.fontFamily = 'Share Tech Mono, monospace';
    
    // Header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr style="background: linear-gradient(180deg, #2a2a2a 0%, #0d1530 100%); border-bottom: 2px solid #d4af37; display: table; width: 100%; table-layout: fixed;">
        <th style="padding: 15px; color: #d4af37; font-family: Rajdhani, Arial, sans-serif; font-size: 14px; text-transform: uppercase; text-align: left; width: 30%;">NAZWA STOŁU</th>
        <th style="padding: 15px; color: #d4af37; font-family: Rajdhani, Arial, sans-serif; font-size: 14px; text-transform: uppercase; text-align: center; width: 15%;">GRACZE</th>
        <th style="padding: 15px; color: #d4af37; font-family: Rajdhani, Arial, sans-serif; font-size: 14px; text-transform: uppercase; text-align: center; width: 18%;">BLINDY</th>
        <th style="padding: 15px; color: #d4af37; font-family: Rajdhani, Arial, sans-serif; font-size: 14px; text-transform: uppercase; text-align: center; width: 20%;">BUY-IN</th>
        <th style="padding: 15px; color: #d4af37; font-family: Rajdhani, Arial, sans-serif; font-size: 14px; text-transform: uppercase; text-align: center; width: 17%;">STATUS</th>
      </tr>
    `;
    table.appendChild(thead);
    
    // Body with scrollable content
    const tbody = document.createElement('tbody');
    tbody.style.display = 'block';
    tbody.style.maxHeight = '320px';
    tbody.style.overflowY = 'auto';
    tbody.style.overflowX = 'hidden';
    
    // Style scrollbar
    tbody.style.scrollbarWidth = 'thin';
    tbody.style.scrollbarColor = '#d4af37 #0d1530';
    
    if (this.availableTables.length === 0) {
      const emptyRow = document.createElement('tr');
      emptyRow.innerHTML = `
        <td colspan="5" style="padding: 40px; color: #666; text-align: center; font-style: italic;">
          Brak dostępnych stołów. Stwórz nowy stół!
        </td>
      `;
      tbody.appendChild(emptyRow);
    } else {
      this.availableTables.forEach((tableData, index) => {
        const row = document.createElement('tr');
        row.style.cursor = 'pointer';
        row.style.transition = 'background-color 0.2s';
        row.style.backgroundColor = index === this.selectedTableIndex ? 'rgba(212, 175, 55, 0.2)' : 'transparent';
        row.style.borderBottom = '1px solid #333';
        row.style.display = 'table';
        row.style.width = '100%';
        row.style.tableLayout = 'fixed';
        
        const playerCount = tableData.players?.filter(p => p !== null).length || 0;
        const maxPlayers = tableData.maxPlayers || 8;
        const smallBlind = tableData.smallBlind || 10;
        const bigBlind = tableData.bigBlind || 20;
        const minBuyin = tableData.minBuyin || 100;
        const maxBuyin = tableData.maxBuyin || 1000;
        const isInProgress = tableData.phase && tableData.phase !== 'waiting';
        const isLoosePlay = this.isLoosePlayTable(tableData);
        const tableName = tableData.tableName || 'Stół';
        const looseBadge = isLoosePlay
          ? '<div style="margin-top: 4px; color: #8fd3ff; font-size: 11px; text-transform: uppercase;">Luźna gra</div>'
          : '';
        
        row.innerHTML = `
          <td style="padding: 12px 15px; color: #ccc; text-align: left; width: 30%;">${tableName}${looseBadge}</td>
          <td style="padding: 12px 15px; color: ${playerCount >= maxPlayers ? '#ff6b6b' : '#6bcf7f'}; text-align: center; width: 15%;">${playerCount}/${maxPlayers}</td>
          <td style="padding: 12px 15px; color: #aaa; text-align: center; width: 18%;">${smallBlind}/${bigBlind}</td>
          <td style="padding: 12px 15px; color: #aaa; text-align: center; width: 20%;">${minBuyin}-${maxBuyin}</td>
          <td style="padding: 12px 15px; text-align: center; width: 17%;">
            <span style="color: ${isInProgress ? '#ffd93d' : '#6bcf7f'}; font-size: 12px;">
              ${isInProgress ? 'W GRZE' : 'OCZEKUJE'}
            </span>
          </td>
        `;
        
        row.addEventListener('mouseover', () => {
          if (index !== this.selectedTableIndex) {
            row.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
          }
        });
        
        row.addEventListener('mouseout', () => {
          row.style.backgroundColor = index === this.selectedTableIndex ? 'rgba(212, 175, 55, 0.2)' : 'transparent';
        });
        
        row.addEventListener('click', () => {
          this.selectTable(index);
        });
        
        tbody.appendChild(row);
      });
    }
    
    // Make header row fixed width
    const theadRow = thead.querySelector('tr') as HTMLTableRowElement;
    if (theadRow) {
      theadRow.style.display = 'table';
      theadRow.style.width = '100%';
      theadRow.style.tableLayout = 'fixed';
    }
    
    table.appendChild(tbody);
    container.appendChild(table);
    
    this.tableListContainer = container;
    document.body.appendChild(container);
  }

  private selectTable(index: number): void {
    this.selectedTableIndex = index;
    // Refresh table to show selection
    this.createTableListHTML();
    
    const selectedTable = this.availableTables[index];
    if (selectedTable) {
      const looseSuffix = this.isLoosePlayTable(selectedTable) ? ' (Luźna gra)' : '';
      this.statusText.setText(`Wybrano: ${selectedTable.tableName}${looseSuffix}`).setColor('#d4af37').setVisible(true);
    }
  }

  /**
   * Format number with Polish formatting (space every 3 digits)
   */
  private formatPolishNumber(num: number): string {
    const rounded = Math.round(num);
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  private isLoosePlayTable(table: PokerTableState): boolean {
    return table.loosePlay ?? table.isLoosePlay ?? false;
  }

  /**
   * Create PC balance display in top-left corner
   */
  private createPCBalanceDisplay(): void {
    this.coinBalanceContainer = this.add.container(70, 60);
    this.coinBalanceContainer.setDepth(100);
    this.coinBalanceContainer.setVisible(false); // Hidden until authenticated

    // Background asset (256x256 scaled to 50%)
    const bgImage = this.add.image(64, 64, 'pc_acc').setOrigin(0.5);
    bgImage.setScale(0.5);
    
    // Label "Saldo" above balance
    const saldoLabel = this.add.text(64, 35, 'Saldo', {
      fontSize: '14px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
      align: 'center',
    }).setOrigin(0.5);
    
    // Balance text - centered on the asset
    this.coinBalanceText = this.add.text(64, 64, '0', {
      fontSize: '21px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
      align: 'center',
    }).setOrigin(0.5);
    
    this.coinBalanceContainer.add([bgImage, saldoLabel, this.coinBalanceText]);
  }

  /**
   * Update PC balance display
   */
  private updatePCBalance(balance: number): void {
    if (this.coinBalanceText) {
      this.coinBalanceText.setText(this.formatPolishNumber(balance));
    }
    if (this.coinBalanceContainer && balance >= 0) {
      this.coinBalanceContainer.setVisible(true);
    }
  }

  private async showTableListView(): Promise<void> {
    if (!this.authenticated) {
      this.statusText.setText('Najpierw połącz z serwerem').setColor('#ff6b6b').setVisible(true);
      return;
    }

    this.statusText.setText('Ładowanie stołów...').setColor('#ffd93d').setVisible(true);
    
    // Load tables from server
    const tables = await pokerClient.getTables(pokerClient.guildId);
    this.availableTables = tables || [];
    this.selectedTableIndex = -1;
    
    // Hide main menu, show table list
    this.mainMenuGroup.setVisible(false);
    this.tableListGroup.setVisible(true);
    
    // Create HTML table
    this.createTableListHTML();
    
    if (this.availableTables.length === 0) {
      this.statusText.setText('Brak dostępnych stołów').setColor('#ffd93d').setVisible(true);
    } else {
      this.statusText.setText(`Znaleziono ${this.availableTables.length} stół(ów)`).setColor('#6bcf7f').setVisible(true);
    }
  }

  private hideTableListView(): void {
    // Show main menu, hide table list
    this.mainMenuGroup.setVisible(true);
    this.tableListGroup.setVisible(false);
    
    // Remove HTML table and cleanup event listeners
    if (this.tableListContainer) {
      if ((this.tableListContainer as any)._cleanup) {
        (this.tableListContainer as any)._cleanup();
      }
      this.tableListContainer.remove();
      this.tableListContainer = null;
    }
    
    this.selectedTableIndex = -1;
    this.statusText.setVisible(false);
  }

  private async joinSelectedTable(): Promise<void> {
    if (this.selectedTableIndex < 0 || this.selectedTableIndex >= this.availableTables.length) {
      this.statusText.setText('Wybierz stół z listy').setColor('#ff6b6b').setVisible(true);
      return;
    }
    
    const table = this.availableTables[this.selectedTableIndex];
    await this.joinTable(table);
  }

  private setupClientEvents(): void {
    pokerClient.onLog = (msg) => {
      // Log handled by PokerClient
      this.statusText.setText(msg).setVisible(true);
    };

    pokerClient.onError = (msg) => {
      console.error('[Client]', msg);
      this.statusText.setText(`Błąd: ${msg}`).setColor('#ff6b6b').setVisible(true);
    };

    pokerClient.onDisconnected = () => {
      this.connected = false;
      this.authenticated = false;
      this.authSessionActive = true;
      this.connectionStatus.setText('Rozłączono').setColor('#ff6b6b').setVisible(true);
      this.coinBalanceContainer?.setVisible(false);
      this.disableMainMenuButtons();
      this.updateAuthButton();
    };

    // Listen for PC balance changes
    pokerClient.onCoinBalanceChanged = (balance: number) => {
      this.updatePCBalance(balance);
    };

    pokerClient.onTableStateChanged = (state) => {
      // TODO: Switch to game scene when joined
    };
  }

  private async autoConnect(): Promise<void> {
    try {
      const response = await fetch(buildHoldemBackendUrl('/api/me'), {
        credentials: 'include'
      });

      const data = await response.json() as HoldemMeResponse;
      const sessionUser = data.user || null;

      if (data.authenticated && sessionUser && data.guildSelectionRequired) {
        const guildOptions = Array.isArray(data.guilds) ? data.guilds : [];
        const hasSelectableGuild = guildOptions.some((guild) => guild.selectable);
        this.connected = false;
        this.authenticated = false;
        this.authSessionActive = true;
        this.pendingSessionUser = sessionUser;
        this.disableMainMenuButtons();
        this.applyDiscordAvatarFromSession(sessionUser.userId, sessionUser.avatar, sessionUser.discriminator);
        this.updateAuthButton();
        this.connectionStatus.setText('Wybierz serwer').setColor('#ffd93d').setVisible(true);
        this.statusText
          .setText(hasSelectableGuild
            ? 'Wybierz serwer, na którym chcesz grać.'
            : 'Żaden z Twoich serwerów z Clair nie ma aktywnego premium.')
          .setColor(hasSelectableGuild ? '#d4d4d4' : '#ffcf75')
          .setVisible(true);
        this.showGuildSelection(guildOptions);
        return;
      }

      if (data.authenticated && sessionUser && !data.guildSelectionRequired) {
        const displayName = sessionUser.nickname || sessionUser.username;
        const guildId = data.selectedGuildId || sessionUser.guildId || '';

        if (!guildId) {
          this.authSessionActive = true;
          this.pendingSessionUser = sessionUser;
          this.updateAuthButton();
          this.connectionStatus.setText('Brak serwera').setColor('#ff6b6b').setVisible(true);
          this.statusText.setText('Sesja logowania nie zawiera wybranego serwera.').setColor('#ff6b6b').setVisible(true);
          return;
        }

        this.authSessionActive = true;
        this.pendingSessionUser = sessionUser;
        this.hideGuildSelection();
        this.applyDiscordAvatarFromSession(sessionUser.userId, sessionUser.avatar, sessionUser.discriminator);
        await this.connectWithCredentials(sessionUser.userId, displayName, guildId);
        return;
      }

      this.connected = false;
      this.authenticated = false;
      this.authSessionActive = false;
      this.pendingSessionUser = null;
      this.hideGuildSelection();
      this.disableMainMenuButtons();
      this.updateAuthButton();
    } catch (error) {
      console.error('[MenuScene] Error checking session:', error);
      this.connectionStatus.setText('Błąd sesji').setColor('#ff6b6b').setVisible(true);
    }
  }

  private applyDiscordAvatarFromSession(
    userId: string,
    avatarHash?: string | null,
    discriminator?: string | null,
  ): void {
    const avatarUrl = buildDiscordAvatarUrl(userId, avatarHash, discriminator);
    if (!avatarUrl) return;

    this.avatarSettings = {
      ...this.avatarSettings,
      discordAvatarUrl: avatarUrl,
    };
    saveAvatarSettings(this.avatarSettings);

    if (this.settingsPanelGroup) {
      this.ensureDiscordAvatarTexture(avatarUrl);
      this.updateAvatarOptionStyles();
    }
  }
  
  private async connectWithCredentials(userId: string, displayName: string, guildId: string): Promise<void> {
    if (!guildId) {
      this.connectionStatus.setText('Brak serwera').setColor('#ff6b6b').setVisible(true);
      this.statusText.setText('Nie wybrano serwera do połączenia.').setColor('#ff6b6b').setVisible(true);
      return;
    }

    this.authSessionActive = true;
    this.hideGuildSelection();
    this.updateAuthButton();
    this.connectionStatus.setText('Łączenie...').setColor('#ffd93d').setVisible(true);
    
    const connected = await pokerClient.connect();
    if (!connected) {
      this.connectionStatus.setText('Błąd połączenia').setColor('#ff6b6b').setVisible(true);
      return;
    }

    this.connected = true;
    this.connectionStatus.setText('Autoryzacja...').setColor('#ffd93d').setVisible(true);

    const authResult = await pokerClient.authenticate();
    if (!authResult.success) {
      this.connectionStatus.setText('Błąd autoryzacji').setColor('#ff6b6b').setVisible(true);
      return;
    }

    // Store display name in poker client for later use
    pokerClient.setUsername(displayName);

    this.authenticated = true;
    this.pendingSessionUser = {
      ...(this.pendingSessionUser || { userId, username: displayName }),
      userId,
      username: this.pendingSessionUser?.username || displayName,
      nickname: displayName,
      guildId,
    };
    
    this.connectionStatus.setText(`Zalogowano jako: ${displayName}`).setColor('#6bcf7f').setVisible(true);
    
    // Fetch server poker configuration
    await this.fetchServerConfig(guildId);
    
    // Enable main menu buttons
    this.enableMainMenuButtons();
    
    // Update PC balance
    this.updatePCBalance(pokerClient.coinBalance);
    
    // Update auth button to show logout
    this.updateAuthButton();
  }

  private showGuildSelection(guilds: HoldemGuildOption[]): void {
    this.guildSelectionOptions = guilds;

    if (this.guildSelectionContainer) {
      if ((this.guildSelectionContainer as any)._cleanup) {
        (this.guildSelectionContainer as any)._cleanup();
      }
      this.guildSelectionContainer.remove();
      this.guildSelectionContainer = null;
    }

    const container = document.createElement('div');
    container.id = 'guild-selection-container';

    const gameCanvas = document.querySelector('#game-container canvas') as HTMLCanvasElement | null;
    const gameContainer = document.getElementById('game-container');

    container.style.position = 'absolute';
    container.style.pointerEvents = 'auto';
    container.style.zIndex = '20';

    const updatePosition = () => {
      if (!gameCanvas || !gameContainer) return;

      const canvasRect = gameCanvas.getBoundingClientRect();
      const scaleX = canvasRect.width / 1920;
      const scaleY = canvasRect.height / 1080;
      const scale = Math.min(scaleX, scaleY);

      const panelWidth = Math.min(820 * scale, canvasRect.width - (40 * scale));
      const panelHeight = Math.min(540 * scale, canvasRect.height - (200 * scale));

      container.style.left = `${canvasRect.left + (canvasRect.width / 2) - (panelWidth / 2)}px`;
      container.style.top = `${canvasRect.top + (170 * scale)}px`;
      container.style.width = `${panelWidth}px`;
      container.style.maxHeight = `${panelHeight}px`;
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    (container as any)._cleanup = () => window.removeEventListener('resize', updatePosition);

    container.style.background = 'linear-gradient(180deg, rgba(5, 8, 20, 0.96) 0%, rgba(9, 16, 34, 0.96) 100%)';
    container.style.border = '2px solid #d4af37';
    container.style.borderRadius = '12px';
    container.style.boxShadow = '0 0 35px rgba(212, 175, 55, 0.28)';
    container.style.padding = '26px';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '16px';
    container.style.overflow = 'hidden';

    const title = document.createElement('div');
    title.innerText = 'WYBIERZ SERWER';
    title.style.color = '#d4af37';
    title.style.fontSize = '28px';
    title.style.fontFamily = 'Rajdhani, Arial, sans-serif';
    title.style.fontWeight = '700';
    title.style.textAlign = 'center';
    title.style.letterSpacing = '0.08em';

    const subtitle = document.createElement('div');
    subtitle.innerText = 'Pokazujemy tylko serwery, na których Clair jest zainstalowana.';
    subtitle.style.color = '#d4d4d4';
    subtitle.style.fontSize = '14px';
    subtitle.style.fontFamily = 'Share Tech Mono, monospace';
    subtitle.style.textAlign = 'center';

    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(180px, 1fr))';
    grid.style.gap = '16px';
    grid.style.overflowY = 'auto';
    grid.style.paddingRight = '4px';
    grid.style.scrollbarWidth = 'thin';
    grid.style.scrollbarColor = '#d4af37 #0d1530';

    const formatPlanTier = (planTier: string): string => {
      if (planTier === 'premium_plus') return 'Premium+';
      if (planTier === 'premium_lite') return 'Premium Lite';
      return 'Free';
    };

    if (guilds.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.innerText = 'Brak serwerów z Clair przypisanych do tego konta.';
      emptyState.style.color = '#ffcf75';
      emptyState.style.fontSize = '16px';
      emptyState.style.fontFamily = 'Rajdhani, Arial, sans-serif';
      emptyState.style.textAlign = 'center';
      emptyState.style.padding = '32px 16px';
      grid.appendChild(emptyState);
    } else {
      guilds.forEach((guild) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.disabled = !guild.selectable || this.guildSelectionInProgress;
        card.style.display = 'flex';
        card.style.flexDirection = 'column';
        card.style.alignItems = 'center';
        card.style.gap = '10px';
        card.style.minHeight = '220px';
        card.style.padding = '18px 16px';
        card.style.borderRadius = '10px';
        card.style.border = guild.selectable ? '1px solid #d4af37' : '1px solid #5a5a5a';
        card.style.background = guild.selectable ? 'rgba(17, 27, 53, 0.9)' : 'rgba(25, 25, 30, 0.92)';
        card.style.cursor = guild.selectable ? 'pointer' : 'not-allowed';
        card.style.opacity = guild.selectable ? '1' : '0.72';
        card.style.transition = 'transform 0.15s ease, border-color 0.15s ease, background 0.15s ease';

        const avatar = document.createElement('div');
        avatar.style.width = '72px';
        avatar.style.height = '72px';
        avatar.style.borderRadius = '50%';
        avatar.style.display = 'flex';
        avatar.style.alignItems = 'center';
        avatar.style.justifyContent = 'center';
        avatar.style.background = 'linear-gradient(180deg, #1d315f 0%, #0d1530 100%)';
        avatar.style.border = '2px solid #d4af37';
        avatar.style.color = '#ffffff';
        avatar.style.fontSize = '28px';
        avatar.style.fontFamily = 'Rajdhani, Arial, sans-serif';
        avatar.style.fontWeight = '700';
        avatar.style.overflow = 'hidden';

        if (guild.iconUrl) {
          const image = document.createElement('img');
          image.src = guild.iconUrl;
          image.alt = guild.name;
          image.style.width = '100%';
          image.style.height = '100%';
          image.style.objectFit = 'cover';
          avatar.appendChild(image);
        } else {
          avatar.innerText = (guild.shortLabel || guild.name.slice(0, 2) || 'S').toUpperCase();
        }

        const blockedText = document.createElement('div');
        blockedText.innerText = guild.blockedReason ? `(${guild.blockedReason})` : ' ';
        blockedText.style.minHeight = '20px';
        blockedText.style.color = guild.blockedReason ? '#ff8f8f' : '#6bcf7f';
        blockedText.style.fontSize = '12px';
        blockedText.style.fontFamily = 'Share Tech Mono, monospace';
        blockedText.style.textTransform = 'uppercase';

        const name = document.createElement('div');
        name.innerText = guild.name;
        name.style.color = '#ffffff';
        name.style.fontSize = '18px';
        name.style.fontFamily = 'Rajdhani, Arial, sans-serif';
        name.style.fontWeight = '700';
        name.style.textAlign = 'center';
        name.style.lineHeight = '1.2';

        const tier = document.createElement('div');
        tier.innerText = formatPlanTier(guild.planTier);
        tier.style.color = guild.selectable ? '#d4af37' : '#999999';
        tier.style.fontSize = '12px';
        tier.style.fontFamily = 'Share Tech Mono, monospace';
        tier.style.textTransform = 'uppercase';
        tier.style.letterSpacing = '0.06em';

        const action = document.createElement('div');
        action.innerText = guild.selectable ? 'Kliknij, aby wejść' : 'Premium wymagane';
        action.style.marginTop = 'auto';
        action.style.color = guild.selectable ? '#6bcf7f' : '#b0b0b0';
        action.style.fontSize = '12px';
        action.style.fontFamily = 'Share Tech Mono, monospace';
        action.style.textAlign = 'center';

        card.appendChild(avatar);
        card.appendChild(blockedText);
        card.appendChild(name);
        card.appendChild(tier);
        card.appendChild(action);

        if (guild.selectable) {
          card.addEventListener('mouseenter', () => {
            card.style.transform = 'translateY(-2px)';
            card.style.borderColor = '#ffe28c';
            card.style.background = 'rgba(24, 38, 73, 0.96)';
          });
          card.addEventListener('mouseleave', () => {
            card.style.transform = 'translateY(0)';
            card.style.borderColor = '#d4af37';
            card.style.background = 'rgba(17, 27, 53, 0.9)';
          });
          card.addEventListener('click', () => {
            void this.selectGuild(guild);
          });
        }

        grid.appendChild(card);
      });
    }

    container.appendChild(title);
    container.appendChild(subtitle);
    container.appendChild(grid);

    this.guildSelectionContainer = container;
    document.body.appendChild(container);
  }

  private hideGuildSelection(): void {
    if (!this.guildSelectionContainer) {
      this.guildSelectionOptions = [];
      this.guildSelectionInProgress = false;
      return;
    }

    if ((this.guildSelectionContainer as any)._cleanup) {
      (this.guildSelectionContainer as any)._cleanup();
    }

    this.guildSelectionContainer.remove();
    this.guildSelectionContainer = null;
    this.guildSelectionOptions = [];
    this.guildSelectionInProgress = false;
  }

  private async selectGuild(guild: HoldemGuildOption): Promise<void> {
    if (!guild.selectable || this.guildSelectionInProgress) {
      return;
    }

    this.guildSelectionInProgress = true;
    this.showGuildSelection(this.guildSelectionOptions);
    this.connectionStatus.setText(`Finalizowanie logowania: ${guild.name}`).setColor('#ffd93d').setVisible(true);
    this.statusText.setText('Trwa przygotowanie połączenia z wybranym serwerem...').setColor('#d4d4d4').setVisible(true);

    try {
      const response = await fetch(buildHoldemBackendUrl('/api/select-guild'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ guildId: guild.id }),
      });

      const payload = await response.json();

      if (!response.ok || !payload?.ok || !payload?.data?.user) {
        const message = payload?.error?.message || 'Nie udało się wybrać serwera.';
        this.connectionStatus.setText('Wybór serwera nieudany').setColor('#ff6b6b').setVisible(true);
        this.statusText.setText(message).setColor('#ff6b6b').setVisible(true);
        this.guildSelectionInProgress = false;
        this.showGuildSelection(this.guildSelectionOptions);
        return;
      }

      const selectedUser = payload.data.user as HoldemSessionUser;
      const selectedGuildId = String(payload.data.selectedGuildId || guild.id);
      this.pendingSessionUser = selectedUser;
      this.applyDiscordAvatarFromSession(selectedUser.userId, selectedUser.avatar, selectedUser.discriminator);
      await this.connectWithCredentials(
        selectedUser.userId,
        selectedUser.nickname || selectedUser.username,
        selectedGuildId,
      );
    } catch (error) {
      console.error('[MenuScene] Error selecting guild:', error);
      this.connectionStatus.setText('Błąd wyboru serwera').setColor('#ff6b6b').setVisible(true);
      this.statusText.setText('Nie udało się dokończyć logowania.').setColor('#ff6b6b').setVisible(true);
      this.guildSelectionInProgress = false;
      this.showGuildSelection(this.guildSelectionOptions);
    }
  }
  
  private async fetchServerConfig(guildId: string): Promise<void> {
    try {
      const apiUrl = `${POKER_API_BASE_URL}/poker/config/${guildId}`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        console.warn('[MenuScene] Failed to fetch server config:', response.statusText);
        return;
      }
      
      const data = await response.json();
      if (data.success && data.config) {
        this.serverConfig = {
          smallBlind: data.config.smallBlind || 5,
          bigBlind: data.config.bigBlind || 10,
          minBuyin: data.config.minBuyin || 100,
          maxBuyin: data.config.maxBuyin || 200,
          maxPlayers: data.config.maxPlayersPerTable || 9
        };
      }
    } catch (error) {
      console.error('[MenuScene] Error fetching server config:', error);
    }
  }
  
  private enableMainMenuButtons(): void {
    this.createTableBtn.setInteractive({ useHandCursor: true });
    this.joinTableBtn.setInteractive({ useHandCursor: true });
    this.createTableBtn.clearTint();
    this.joinTableBtn.clearTint();
  }

  private disableMainMenuButtons(): void {
    this.createTableBtn.disableInteractive();
    this.joinTableBtn.disableInteractive();
    this.createTableBtn.setTint(0x666666);
    this.joinTableBtn.setTint(0x666666);
  }

  private async createAndJoinTable(tableConfig: {
    name: string;
    maxPlayers: number;
    smallBlind: number;
    bigBlind: number;
    minBuyin: number;
    maxBuyin: number;
    loosePlay: boolean;
  }): Promise<void> {
    if (!this.authenticated) {
      this.statusText.setText('Najpierw połącz z serwerem').setColor('#ff6b6b').setVisible(true);
      return;
    }

    this.statusText.setText('Tworzenie stołu...').setColor('#ffd93d').setVisible(true);

    const table = await pokerClient.createTable(pokerClient.guildId, {
      name: tableConfig.name,
      maxPlayers: tableConfig.maxPlayers,
      bigBlind: tableConfig.bigBlind,
      smallBlind: tableConfig.smallBlind,
      minBuyin: tableConfig.minBuyin,
      maxBuyin: tableConfig.maxBuyin,
      loosePlay: tableConfig.loosePlay,
    });

    if (!table) {
      this.statusText.setText('Błąd tworzenia stołu!').setColor('#ff6b6b');
      return;
    }

    if (tableConfig.loosePlay) {
      table.loosePlay = true;
      table.isLoosePlay = true;
    }

    await this.joinTable(table);
  }

  private async joinTable(table: PokerTableState): Promise<void> {
    // Check if already at a table
    if (pokerClient.tableId && pokerClient.tableId !== table.tableId) {
      this.showConfirmPopup(table);
      return;
    }
    
    await this.doJoinTable(table);
  }

  private async doJoinTable(table: PokerTableState): Promise<void> {
    // Don't join table here - just go to GameScene where player will select buy-in
    this.statusText.setText('Ładowanie stołu...').setColor('#ffd93d').setVisible(true);
    
    // Transition to game scene - buy-in selection will happen there
    this.goToGameScene(table);
  }

  // Old doJoinTable logic kept for reference but not used:
  // Player selects buy-in in GameScene now
  private async doJoinTableWithBuyin(table: PokerTableState, buyin: number): Promise<void> {
    const username = pokerClient.username; // Discord username from session
    
    // Calculate next available seat (players count + 1)
    const seatNumber = table.players.length + 1;

    this.statusText.setText('Dołączanie do stołu...').setColor('#ffd93d').setVisible(true);

    const result = await pokerClient.joinTable({
      tableId: table.tableId,
      buyin: buyin,
      username: username,
      avatar: getSelectedAvatarValue(this.avatarSettings),
      seatNumber: seatNumber,
    });

    if (!result.success) {
      // If seat already taken error, try to leave first and rejoin
      if (result.error?.includes('seat') || result.error?.includes('already')) {
        this.statusText.setText('Opuszczanie poprzedniego stołu...').setColor('#ffd93d').setVisible(true);
        await pokerClient.leaveTable();
        
        // Retry join with same seat number
        const retryResult = await pokerClient.joinTable({
          tableId: table.tableId,
          buyin: buyin,
          username: username,
          avatar: getSelectedAvatarValue(this.avatarSettings),
          seatNumber: seatNumber,
        });
        
        if (!retryResult.success) {
          this.statusText.setText(`Błąd: ${retryResult.error}`).setColor('#ff6b6b');
          return;
        }
        
        this.statusText.setText(`Dołączono! Miejsce: ${retryResult.seatNumber}`).setColor('#6bcf7f');
        
        // Transition to game scene
        this.goToGameScene(table);
        return;
      }
      
      this.statusText.setText(`Błąd: ${result.error}`).setColor('#ff6b6b');
      return;
    }

    this.statusText.setText(`Dołączono! Miejsce: ${result.seatNumber}`).setColor('#6bcf7f');
    
    // Transition to game scene
    this.goToGameScene(table);
  }

  private goToGameScene(table: PokerTableState): void {
    // Clean up HTML elements before scene transition
    this.cleanupHTMLElements();
    
    // Clear event handlers to prevent errors when scene switches
    pokerClient.onLog = null;
    pokerClient.onError = null;
    pokerClient.onTableStateChanged = null;
    
    // Small delay to show success message
    this.time.delayedCall(500, () => {
      // Pass tableState with minBuyin/maxBuyin for buy-in popup
      this.scene.start('GameScene', { 
        tableState: table,
        playerId: pokerClient.userId,
        maxPlayers: table.maxPlayers,
        tableId: table.tableId
      });
    });
  }

  private cleanupHTMLElements(): void {
    // Remove table list container
    if (this.tableListContainer) {
      if ((this.tableListContainer as any)._cleanup) {
        (this.tableListContainer as any)._cleanup();
      }
      this.tableListContainer.remove();
      this.tableListContainer = null;
    }
    
    // Remove create table form container
    if (this.createTableFormContainer) {
      if ((this.createTableFormContainer as any)._cleanup) {
        (this.createTableFormContainer as any)._cleanup();
      }
      this.createTableFormContainer.remove();
      this.createTableFormContainer = null;
    }

    this.hideGuildSelection();
  }
}
